import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, RadioGroup, Stack, Text, Textarea, useToast } from '@fluent/ui';
// `Text` is imported for the bold legend node passed into RadioGroup.
import { reportError, type FeedbackReason } from '../lib/feedback.js';
import { track } from '../lib/analytics.js';

export interface ReportErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonId: string;
  exerciseId?: string;
  /** Extra context the lesson already knows; merged into `feedback.meta`. */
  meta?: Record<string, unknown>;
}

const REASON_KEYS: FeedbackReason[] = [
  'typo',
  'unclear',
  'wrong_translation',
  'audio_broken',
  'other',
];

const MESSAGE_MAX = 1000;

/**
 * Modal that opens when the user taps the report-error flag in the
 * lesson topbar. Captures one of 5 enum reasons + an optional free-form
 * note. We don't surface success/failure in the body — submitting always
 * shows a thank-you toast (see `lib/feedback.ts` for the rationale).
 */
export function ReportErrorDialog({
  open,
  onOpenChange,
  lessonId,
  exerciseId,
  meta,
}: ReportErrorDialogProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [reason, setReason] = useState<FeedbackReason | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setReason(null);
    setMessage('');
    setSubmitting(false);
  };

  const onSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    track('feedback_submitted', { lesson_id: lessonId, reason });
    await reportError({
      lessonId,
      exerciseId,
      reason,
      message: message.trim() || undefined,
      meta: { locale: i18n.language, ...(meta ?? {}) },
    });
    toast({
      tone: 'success',
      title: t('feedback.thanks_title'),
      message: t('feedback.thanks_body'),
    });
    onOpenChange(false);
    reset();
  };

  const onChangeOpen = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onChangeOpen}
      title={t('feedback.title')}
      description={t('feedback.description')}
      actions={
        <>
          <Button variant="ghost" onClick={() => onChangeOpen(false)}>
            {t('feedback.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!reason}
            loading={submitting}
            onClick={onSubmit}
          >
            {t('feedback.submit')}
          </Button>
        </>
      }
    >
      <Stack gap={4}>
        <RadioGroup
          legend={<Text weight="bold">{t('feedback.reason_legend')}</Text>}
          options={REASON_KEYS.map((r) => ({
            value: r,
            label: t(`feedback.reason.${r}`),
          }))}
          value={reason}
          onChange={setReason}
        />
        <Textarea
          label={t('feedback.message_label')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          placeholder={t('feedback.message_placeholder')}
          maxLength={MESSAGE_MAX}
          showCount
        />
      </Stack>
    </Dialog>
  );
}
